<template>
  <div
    class="statut-card"
    :class="{ 'statut-card--annule': isAnnule }"
  >
    <!-- Intitulé : lecture immédiate de l'état -->
    <div class="statut-card__head">
      <span class="statut-card__eyebrow">Statut</span>
      <span class="statut-card__current">
        <span
          class="statut-card__marker"
          :style="{ backgroundColor: currentColor }"
        />
        <span
          class="statut-card__current-text"
          :style="{ color: currentColor }"
        >{{ currentLabel }}</span>
      </span>
    </div>

    <!-- Stepper du cycle -->
    <ol
      class="statut-stepper"
      :class="{ 'statut-stepper--annule': isAnnule }"
      :style="{ '--statut-color': currentColor }"
    >
      <li
        v-for="step in progression"
        :key="step.id"
        class="statut-stepper__step"
        :class="{
          'is-done': !isAnnule && step.id < current,
          'is-current': !isAnnule && step.id === current,
        }"
      >
        <span class="statut-stepper__dot">
          <feather-icon
            v-if="isAnnule"
            icon="XIcon"
            size="18"
          />
          <feather-icon
            v-else-if="isStepChecked(step)"
            icon="CheckIcon"
            size="18"
          />
        </span>
        <span class="statut-stepper__label">{{ step.valeur }}</span>
      </li>
    </ol>
  </div>
</template>

<script>
// Affichage proéminent du statut d'un chantier, placé sous le bandeau hero.
// - intitulé du statut courant en grand (couleur du statut) -> état compris au premier coup d'œil
// - stepper du cycle : progression = statuts d'id <= statut résolu (À faire -> En cours -> Clôturé)
// - dernier statut atteignable atteint (ex. « Clôturé ») : bulle cochée + anneau (cycle terminé)
// - état terminal « Annulé » (id > résolu) : intitulé rouge + cycle barré
export default {
  name: 'ChantierStatutStepper',
  props: {
    statuts: { type: Array, required: true }, // [{ id, valeur, couleur, resolu, defaut }]
    current: { type: Number, default: null }, // chantier.statut
  },
  computed: {
    sortedStatuts() {
      return [...this.statuts].sort((a, b) => a.id - b.id)
    },
    resoluStatut() {
      return this.statuts.find(s => s.resolu) || null
    },
    progression() {
      if (!this.resoluStatut) return this.sortedStatuts
      return this.sortedStatuts.filter(s => s.id <= this.resoluStatut.id)
    },
    lastReachableId() {
      const p = this.progression
      return p.length ? p[p.length - 1].id : null
    },
    annuleStatut() {
      if (!this.resoluStatut) return null
      return this.sortedStatuts.find(s => s.id > this.resoluStatut.id) || null
    },
    isAnnule() {
      return this.annuleStatut != null && this.current === this.annuleStatut.id
    },
    currentStatut() {
      return this.statuts.find(st => st.id === this.current) || null
    },
    currentColor() {
      return this.currentStatut ? this.currentStatut.couleur : '#5A7373'
    },
    currentLabel() {
      return this.currentStatut ? this.currentStatut.valeur : '—'
    },
  },
  methods: {
    // Coche sur toutes les étapes franchies, ET sur le dernier statut atteignable
    // une fois atteint (ex. « Clôturé ») -> cycle terminé.
    // L'anneau « étape courante » (is-current) reste géré séparément dans le template.
    isStepChecked(step) {
      if (this.isAnnule) return false
      return step.id < this.current
        || (step.id === this.current && step.id === this.lastReachableId)
    },
  },
}
</script>

<style lang="scss" scoped>
// Affiché sous le bandeau hero, sur fond clair $body-bg #f8f8f8 (carte blanche).
// Palette claire (ink), tailles agrandies pour une lecture immédiate.
.statut-card {
  background: #fff;
  border: 1px solid #e4ecec;            // ink-100
  border-radius: 10px;
  padding: 22px 24px;
  margin-bottom: 18px;

  &__head {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 18px;
  }
  &__eyebrow {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #5a7373;                     // ink-500
  }
  &__current {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }
  &__marker {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    flex: 0 0 auto;
  }
  &__current-text {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.1;
  }
}

.statut-stepper {
  display: flex;
  align-items: flex-start;
  list-style: none;
  margin: 0;
  padding: 0;

  &__step {
    position: relative;
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    min-width: 88px;

    // connecteur vers l'étape précédente (dot 42px -> centre 21px ; trait 3px)
    &:not(:first-child)::before {
      content: '';
      position: absolute;
      top: 20px;
      right: 50%;
      left: -50%;
      height: 3px;
      background: #e4ecec;              // rail gris (lisible sur blanc)
      z-index: 0;
    }
  }
  &__dot {
    position: relative;
    z-index: 1;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #e4ecec;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
  }
  &__label {
    font-size: 14px;
    font-weight: 600;
    color: #5a7373;                     // ink-500 (étapes à venir)
    line-height: 1.3;
  }

  // étapes atteintes : couleur du statut courant
  &__step.is-done,
  &__step.is-current {
    .statut-stepper__dot {
      background: var(--statut-color, #1c4d4d);
      border-color: var(--statut-color, #1c4d4d);
    }
    .statut-stepper__label { color: #2a3f3f; }   // ink-700
  }
  &__step.is-done::before,
  &__step.is-current::before {
    background: var(--statut-color, #1c4d4d);
  }
  // étape courante très marquée : anneau coloré (taille des dots inchangée -> rail aligné)
  &__step.is-current {
    .statut-stepper__dot {
      box-shadow: 0 0 0 4px #fff, 0 0 0 6px var(--statut-color, #1c4d4d);
    }
    .statut-stepper__label {
      color: #0f1f1f;                   // ink-900
      font-weight: 700;
      font-size: 15px;
    }
  }

  // cas Annulé : cycle barré
  &--annule {
    .statut-stepper__dot {
      border-style: dashed;
      border-color: #cfd6d6;
      color: #9aa5b1;
    }
    .statut-stepper__step:not(:first-child)::before {
      background: transparent;
      border-top: 3px dashed #cfd6d6;
      height: 0;
    }
    .statut-stepper__label {
      color: #9aa5b1;
      text-decoration: line-through;
    }
  }
}
</style>
